import { useInfiniteQuery, useMutation, UseMutationOptions, useQuery, UseQueryOptions } from '@tanstack/react-query'
import qs from 'qs'
import { ReactNode } from 'react'
import { OffsetLimit, OffsetLimitPaginationResponse } from '~/api/common'
import {
  ApplicableCandidate,
  ApplyJobApplicationResponse,
  JobApplicationApplyError,
  JobApplicationOptions,
  JobApplicationStatus,
  OfferStatus
} from '~/api/jobApplications'
import { VendorDistroJobResponseStatus } from '~/api/jobDistribution'
import { ShiftApiData } from '~/api/shifts'
import { sowJobApi } from '~/api/sow/jobs'
import { ChargeCodeRawValues } from '~/charge_codes/types'
import { formatDataDate } from '~/common/dateUtils'
import { fetchWithAlerts } from '~/common/fetchUtils'
import { lazyMsg } from '~/common/localizationUtils'
import { isClient } from '~/common/nextJsUtils'
import { floatToPercent } from '~/common/numberUtils'
import { queryClient } from '~/common/react-query/queryClient'
import { ActivityLogType } from '~/components/v2/ApprovalActivityDrawer'
import { ShareProfileModalFormData } from '~/components/v2/ShareProfileModal/ShareProfileModal'
import { RateCalculatorData } from '~/job/rate_calculator/types'
import { EventBasedExpensesListApiData } from '~/settings/event_based_expenses/types'
import fetch from '../common/fetch'

export type JobListConfig = {
  is_shortlisting_enabled: boolean
  is_card_view_enabled: boolean
  is_job_resource_manager_visible: boolean
  is_business_unit_visible: boolean
  is_program_team_visible: boolean
  is_provisioning_enabled: boolean
  program_team_label: string
  show_experience: boolean
  show_weekly_paid_travel: boolean
  close_position_for_all_workflows: boolean
  can_view_calculation_pack: boolean
  is_vendor_visible: boolean
  time_entry_unit: TimeEntryUnit
  show_job_code: boolean
  show_expense_type: boolean
  show_program_team: boolean
  show_hiring_team: boolean
  show_timesheet_approver: boolean
  show_expense_approver: boolean
  show_hire_type: boolean
  show_contractor_std_hrs_week: boolean
  show_contract_type: boolean
  show_not_qualified_rate: boolean
  show_preferred_bill_rate: boolean
  show_project: boolean
  show_premium_rate: boolean
}

export type JobConfig = {
  can_clone_job: boolean
  can_email_vendors: boolean
  can_reopen_job: boolean
  display_checklists: boolean
  hide_edit: boolean
  is_vendor: boolean
  is_preid_enabled: boolean
  end_job_reasons: string[]
  end_job_sub_reasons: string[]
  has_pre_onboarding_checklists: boolean
  can_waive_verifications: boolean
  show_waive_verifications: boolean
  can_apply_candidates: boolean
  can_close_unfilled_positions: boolean
  can_view_job_activity_logs: boolean
  close_position_for_all_workflows: boolean
  close_unfilled_positions_on_job: boolean
  event_based_expenses: {
    can_view: boolean
    can_edit: boolean
  }
  has_contractor_bill_rate_read: boolean
  has_vendor_company_read: boolean
  has_work_order_end_date_read: boolean
  is_vendor_visible: boolean
  show_recommendations: boolean
  remove_recommendation: boolean
  unfilled_positions_count: number
  show_candidate_experience: boolean
  show_work_type_profile: boolean
  show_calendar_pay_profile: boolean
  show_overtime_profile: boolean
  show_shifts: boolean
  show_compensation: boolean
}

export type JobFormConfigForBu = {
  can_edit_rate_calculator_job: boolean
  can_override_description: boolean
  can_override_max_duration: boolean
  can_override_shifts: boolean
  can_read_event_based_expenses: boolean
  can_edit_event_based_expenses: boolean
  can_edit_calculation_pack_job: boolean
  can_read_calculation_pack: boolean
}

export interface VendorThresholds {
  apply_maximum_no_of_applications_per_vendor_limit: boolean
  exceeded_or_met_threshold: boolean
  remaining_applications: number
  vendor_ineligible_message: string
}

type MinRateConfig = {
  [key in PayType]: {
    min_rate: number
  }
}

type MaxRateConfig = {
  [key: string]: {
    max_bill_rate: number
  }
}

export type JobCompensationSettings = {
  max_std_hrs_week: number
  hire_type_direct: boolean
  hire_type_contractor: boolean
  force_pay_type: boolean
  force_exemption_status: boolean
  hide_exemption_status_warning: boolean
  allow_pay_rate_override: boolean
  hide_rate_card_max: boolean
  include_pay_rate: boolean
  rate_type: RateType
  rate_config: MinRateConfig
  contractor_pay_types: PayType[]
  force_rate_card_usage: boolean
  budget_calculator: boolean
  enable_contract_to_hire: boolean
  max_std_hrs_day: number
  max_std_days_week: number
  orientation_enabled: boolean
  direct_hire_pay_types: PayType[]
  contractor_vendor_pay_terms: VendorPayment[]
  direct_hire_vendor_pay_terms: VendorPayment[]
  pay_type_hourly_settings: MaxRateConfig
  pay_type_daily_settings: MaxRateConfig
  pay_type_weekly_settings: MaxRateConfig
  pay_type_monthly_settings: MaxRateConfig
  pay_type_milestone_settings: MaxRateConfig
}

type Message = {
  message: string
}

/**
 * Jobs API
 */
export const jobsApi = {
  get: (jobId: number, queryParams?: Record<string, any>): Promise<Job> =>
    fetchWithAlerts(fetch.get, `/api/v2/jobs/job/${jobId}/?${qs.stringify(queryParams, { skipNulls: true })}`),
  list: (queryParams: Record<string, any>): Promise<JobsResponse> =>
    fetch.get(`/api/v2/jobs/list/?${qs.stringify(queryParams, { skipNulls: true })}`),
  listDetail: (jobId: number): Promise<JobApplyModalDetail> => fetch.get(`/api/v2/jobs/list/${jobId}/`),
  listNonEndedCount: (queryParams: Record<string, any>): Promise<number> =>
    fetch.get(`/api/v2/jobs/list/non-ended-count?${qs.stringify(queryParams, { skipNulls: true })}`),
  config: (jobId: number, queryParams: Record<string, any> = {}): Promise<JobConfig> =>
    fetchWithAlerts(fetch.get, `/api/v2/jobs/job/${jobId}/config/?${qs.stringify(queryParams, { skipNulls: true })}`),
  view: (jobId: number, queryParams: Record<string, any>) =>
    fetch.get(`/api/v2/jobs/job/${jobId}/view/?${qs.stringify(queryParams, { skipNulls: true })}`),
  toggleFavorite: (jobId: number) => fetch.post(`/api/v2/jobs/job/${jobId}/toggle_favorite/`),
  toggleHold: (jobId: number) => fetch.put(`/jobs/hold_job/`, { job: jobId }),
  getApplicationStats: (jobId: number): Promise<JobApplicationStats> =>
    fetch.get(`/api/v2/jobs/job/${jobId}/application_stats`),
  getConfig: (): Promise<JobListConfig> => fetch.get('/api/v2/jobs/config'),
  getFormConfigForBu: (buId: number): Promise<JobFormConfigForBu> =>
    fetch.get(`/api/v2/jobs/job_form_config_for_bu/${buId}`),
  getActivityLogs: (jobId: number): Promise<ActivityLogType[]> => fetch.get(`/api/v2/jobs/job/${jobId}/activity_log`),
  getModuleTypes: (): Promise<JobModuleResponse> => fetch.get(`/api/v2/jobs/module_types`),
  getForJobAppList: (jobId: number): Promise<JobForJobAppList> =>
    fetch.get(`/api/v2/jobs/job/${jobId}/job_app_list_details`),
  reopenJob: (jobId: number, data: ReopenJobParams) => fetch.post(`/jobs/reopen-job/${jobId}`, data),
  emailVendors: (jobId: number, data: Message) => fetch.post(`/api/v2/jobs/${jobId}/email_vendors`, data),
  shareCandidateProfile: (data: CandidateProfileShareData) => fetch.post(`/api/v2/jobs/send-cand-email`, data),
  setBulkActionCreatorRead: (jobId: number) => fetch.put(`/api/v2/jobs/job/${jobId}/set_bulk_action_creator_read`),
  getPublishJobHelpText: () => fetch.get('/api/v2/jobs/get_publish_job_help_text'),
  getJobTemplateConfig: () => fetch.get('/api/v2/jobs/template-config'),
  createCustomJob: (data: string) => fetch.post('/api/jobs/job-title-add/', { title: data }),
  applyCandidates: (id: number, body: any) => fetch.post(`/api/v2/jobs/job/${id}/apply-candidates/`, body),
  getJobCompensationSettings: (): Promise<JobCompensationSettings> => fetch.get(`/api/v2/jobs/compensation/settings`)
}

export enum JobStatus {
  DRAFT = 1,
  PENDING_APPROVAL = 2,
  ON_HOLD = 3,
  ACTIVE = 4,
  CLOSED = 5,
  PENDING_VENDOR_ASSIGNMENT = 6,
  PENDING_CONNECTOR = 7,
  REJECTED = 8
}

export enum JobStatusDisplay {
  DRAFT = 'Draft',
  PENDING_APPROVAL = 'Pending Approval',
  ON_HOLD = 'Hold',
  ACTIVE = 'Active',
  CLOSED = 'Closed',
  PENDING_VENDOR_ASSIGNMENT = 'Pending',
  PENDING_CONNECTOR = 'Pending',
  REJECTED = 'Rejected'
}

export const JOB_STATUS_DISPLAY_MAP = {
  [JobStatusDisplay.DRAFT]: lazyMsg('term.draft'),
  [JobStatusDisplay.PENDING_APPROVAL]: lazyMsg('common.badge_status.pending_approval'),
  [JobStatusDisplay.ON_HOLD]: lazyMsg('term.hold'),
  [JobStatusDisplay.ACTIVE]: lazyMsg('term.active'),
  [JobStatusDisplay.CLOSED]: lazyMsg('common.ui.closed'),
  [JobStatusDisplay.PENDING_VENDOR_ASSIGNMENT]: lazyMsg('term.pending'),
  // [JobStatusDisplay.PENDING_CONNECTOR]: lazyMsg('term.pending'), // Same as above, can't have two same keys
  [JobStatusDisplay.REJECTED]: lazyMsg('common.ui.rejected')
}

export enum VendorPayment {
  IMMEDIATE = 1,
  WEEKLY = 2,
  MONTHLY = 3,
  QUARTERLY = 4,
  CUSTOM = 5,
  PRE_NEGOTIATED = 6,
  MILESTONE = 7
}

export enum PayType {
  HOURLY = 1,
  DAILY = 2,
  WEEKLY = 3,
  MONTHLY = 5,
  // FIXED = 4, DELETED FROM SYSTEM
  SALARIED = 6,
  MILESTONE = 7
}

export enum PayTypeDisplay {
  HOUR = 'Per Hour',
  DAILY = 'Per Day',
  WEEKLY = 'Per Week',
  MONTHLY = 'Per Month',
  SALARIED = 'Per Year',
  MILESTONE = 'Per Milestone'
}

export enum RateType {
  MAX_RATE = 1,
  RANGE_OF_RATES = 2,
  NO_RATE = 3,
  SET_RATE = 4
}

export const PAY_TYPE_DISPLAY_MAP = {
  [PayTypeDisplay.HOUR]: 'term.rate.per_hour_display',
  [PayTypeDisplay.DAILY]: 'term.rate.per_day_display',
  [PayTypeDisplay.WEEKLY]: 'term.rate.per_week_display',
  [PayTypeDisplay.MONTHLY]: 'term.rate.per_month_display',
  [PayTypeDisplay.SALARIED]: 'term.rate.per_year_display',
  [PayTypeDisplay.MILESTONE]: 'term.rate.per_milestone_display',
  [PayType.HOURLY]: 'term.rate.per_hour_display',
  [PayType.DAILY]: 'term.rate.per_day_display',
  [PayType.WEEKLY]: 'term.rate.per_week_display',
  [PayType.MONTHLY]: 'term.rate.per_month_display',
  [PayType.SALARIED]: 'term.rate.per_year_display',
  [PayType.MILESTONE]: 'term.rate.per_milestone_display'
}

export const PAY_TYPE_FORMAT_MAP = {
  [PayType.HOURLY]: 'term.rate.format.per_hour',
  [PayType.DAILY]: 'term.rate.format.per_day',
  [PayType.WEEKLY]: 'term.rate.format.per_week',
  [PayType.MONTHLY]: 'term.rate.format.per_month',
  [PayType.SALARIED]: 'term.rate.format.per_year',
  [PayType.MILESTONE]: 'term.rate.format.per_milestone',
  [PayTypeDisplay.HOUR]: 'term.rate.format.per_hour',
  [PayTypeDisplay.DAILY]: 'term.rate.format.per_day',
  [PayTypeDisplay.WEEKLY]: 'term.rate.format.per_week',
  [PayTypeDisplay.MONTHLY]: 'term.rate.format.per_month',
  [PayTypeDisplay.SALARIED]: 'term.rate.format.per_year',
  [PayTypeDisplay.MILESTONE]: 'term.rate.format.per_milestone'
}

export const PAY_TYPE_DISPLAY_FORMAT_MAP = {
  [PayType.HOURLY]: 'term.rate.format.per_hour_display',
  [PayType.DAILY]: 'term.rate.format.per_day_display',
  [PayType.WEEKLY]: 'term.rate.format.per_week_display',
  [PayType.MONTHLY]: 'term.rate.format.per_month_display',
  [PayType.SALARIED]: 'term.rate.format.per_year_display',
  [PayType.MILESTONE]: 'term.rate.format.per_milestone_display',
  [PayTypeDisplay.HOUR]: 'term.rate.format.per_hour_display',
  [PayTypeDisplay.DAILY]: 'term.rate.format.per_day_display',
  [PayTypeDisplay.WEEKLY]: 'term.rate.format.per_week_display',
  [PayTypeDisplay.MONTHLY]: 'term.rate.format.per_month_display',
  [PayTypeDisplay.SALARIED]: 'term.rate.format.per_year_display',
  [PayTypeDisplay.MILESTONE]: 'term.rate.format.per_milestone_display'
}

export enum PayTypeSuffix {
  HOURLY = '/hr',
  DAILY = '/day',
  WEEKLY = '/wk',
  MONTHLY = '/mo',
  SALARIED = '/yr',
  MILESTONE = '/ms'
}

export const PayTypeSuffixLazy = {
  [PayType.HOURLY]: lazyMsg('term.rate.per_hour'),
  [PayType.DAILY]: lazyMsg('term.rate.per_day'),
  [PayType.WEEKLY]: lazyMsg('term.rate.per_week'),
  [PayType.MONTHLY]: lazyMsg('term.rate.per_month'),
  [PayType.SALARIED]: lazyMsg('term.rate.per_year'),
  [PayType.MILESTONE]: lazyMsg('term.rate.per_milestone')
}

export const PAY_TYPE_TOKEN = {
  [PayType.HOURLY]: lazyMsg('term.rate.hourly'),
  [PayType.DAILY]: lazyMsg('term.rate.daily'),
  [PayType.WEEKLY]: lazyMsg('term.rate.weekly'),
  [PayType.MONTHLY]: lazyMsg('term.rate.monthly'),
  [PayType.SALARIED]: lazyMsg('term.rate.yearly'),
  [PayType.MILESTONE]: lazyMsg('term.rate.milestone_payments')
}

export const PayTypeDisplaySuffixLazy = {
  [PayTypeDisplay.HOUR]: lazyMsg('term.rate.per_hour'),
  [PayTypeDisplay.DAILY]: lazyMsg('term.rate.per_day'),
  [PayTypeDisplay.WEEKLY]: lazyMsg('term.rate.per_week'),
  [PayTypeDisplay.MONTHLY]: lazyMsg('term.rate.per_month'),
  [PayTypeDisplay.SALARIED]: lazyMsg('term.rate.per_year'),
  [PayTypeDisplay.MILESTONE]: lazyMsg('term.rate.per_milestone')
}

export const PAY_TYPE_SUFFIX_MAP = {
  [PayType.HOURLY]: PayTypeSuffix.HOURLY,
  [PayType.DAILY]: PayTypeSuffix.DAILY,
  [PayType.WEEKLY]: PayTypeSuffix.WEEKLY,
  [PayType.MONTHLY]: PayTypeSuffix.MONTHLY,
  [PayType.SALARIED]: PayTypeSuffix.SALARIED,
  [PayType.MILESTONE]: PayTypeSuffix.MILESTONE,
  [PayTypeDisplay.HOUR]: PayTypeSuffix.HOURLY,
  [PayTypeDisplay.DAILY]: PayTypeSuffix.DAILY,
  [PayTypeDisplay.WEEKLY]: PayTypeSuffix.WEEKLY,
  [PayTypeDisplay.MONTHLY]: PayTypeSuffix.MONTHLY,
  [PayTypeDisplay.SALARIED]: PayTypeSuffix.SALARIED,
  [PayTypeDisplay.MILESTONE]: PayTypeSuffix.MILESTONE
}

export enum ContractType {
  CONTRACT_ONLY = 1,
  CONTRACT_TO_HIRE = 2
}

export const CONTRACT_TYPE_TOKEN = {
  [ContractType.CONTRACT_ONLY]: lazyMsg('term.contract_only'),
  [ContractType.CONTRACT_TO_HIRE]: lazyMsg('term.contract_to_hire')
}

export enum ConversionType {
  CONVERSION_HOURLY = 1,
  CONVERSION_SALARIED = 2
}

export const CONVERSION_TYPE_TOKEN = {
  [ConversionType.CONVERSION_HOURLY]: lazyMsg('term.rate.hourly'),
  [ConversionType.CONVERSION_SALARIED]: lazyMsg('term.rate.salaried')
}

export enum TimeEntryUnit {
  HOURS = 'hours',
  DAYS = 'days'
}

export enum OtApplicability {
  OT_EXEMPT = 1,
  OT_NON_EXEMPT = 2
}

export const OT_APPLICABILITY_TOKEN = {
  [OtApplicability.OT_EXEMPT]: lazyMsg('term.exempt'),
  [OtApplicability.OT_NON_EXEMPT]: lazyMsg('term.non_exempt')
}

export enum ShiftStrategy {
  DEDICATED = 1,
  FLEXIBLE = 2
}

export const SHIFT_STRATEGY_TOKEN = {
  [ShiftStrategy.DEDICATED]: lazyMsg('shift_strategy.dedicated'),
  [ShiftStrategy.FLEXIBLE]: lazyMsg('shift_strategy.flexible')
}

export enum ProgramRateType {
  BILL_RATE = 'bill_rate',
  PAY_RATE = 'pay_rate'
}

export const PROGRAM_RATE_TYPE_TOKEN = {
  [ProgramRateType.BILL_RATE]: lazyMsg('term.bill_rate_based_program_type'),
  [ProgramRateType.PAY_RATE]: lazyMsg('term.pay_rate_based_program_type')
}

export enum BillingMode {
  BILLING_MODE_SCHEDULED = 1,
  BILLING_MODE_MISC_ADJUSTMENTS = 2,
  BILLING_MODE_TIMESHEETS = 3
}

export const BILLING_MODE_TOKEN = {
  [BillingMode.BILLING_MODE_SCHEDULED]: lazyMsg('scheduled_billing.billing_mode.scheduled'),
  [BillingMode.BILLING_MODE_MISC_ADJUSTMENTS]: lazyMsg('scheduled_billing.billing_mode.misc_adjustments'),
  [BillingMode.BILLING_MODE_TIMESHEETS]: lazyMsg('scheduled_billing.billing_mode.timesheets')
}

export enum JobFlowStatus {
  STATE_INITIAL = 0,
  STATE_VENDOR_APPLIED = 1,
  STATE_CLIENT_INTERVIEWING = 2,
  STATE_CLIENT_REJECTED = 3,
  STATE_PENDING_OFFER_RELEASE = 4,
  STATE_CLIENT_OFFER_RELEASED = 5,
  STATE_VENDOR_OFFER_DECLINED = 6,
  STATE_VENDOR_WITHDRAWN = 7,
  STATE_VENDOR_VERIFICATION_IN_PROGRESS = 8,
  STATE_VENDOR_VERIFICATION_FAILED = 9,
  STATE_PENDING_READY_TO_ONBOARD_APPROVALS = 10,
  STATE_READY_TO_ONBOARD = 11,
  STATE_CONTRACTOR_ONBOARDED = 12,
  STATE_JOB_CLOSED = 13,
  STATE_ONBOARDED_BUT_NOT_ACTIVE = 14,
  STATE_APPROVAL_REJECTED = 15,
  STATE_CONTRACT_END_PENDING = 16,
  STATE_CONTRACT_ENDED = 17,
  STATE_CONTRACT_EDITED = 18,
  STATE_CONTRACT_MODIFICATION_PENDING_CLIENT = 19,
  STATE_CONTRACT_MODIFICATION_PENDING_VENDOR = 20,
  STATE_CONTRACT_REOPEN = 21,
  STATE_CONTRACT_CANCELLED = 22,
  STATE_PRE_ONBOARD_TRANSITION_PROCESSING = 24,
  STATE_PENDING_OFFER_ACCEPTANCE_APPROVALS = 25,
  STATE_CONNECTOR_OFFER_ACCEPTED = 5001,
  STATE_CONNECTOR_FUTURE_ONBOARDING = 5003,
  STATE_CONNECTOR_ONBOARDING = 5004,
  STATE_CONNECTOR_OFFBOARDING = 5005,
  STATE_CONNECTOR_CONTRACT_EDITED = 5006,
  STATE_CONNECTOR_REOPEN = 5007,
  STATE_CONNECTOR_FUTURE_ENDED = 5008,
  STATE_CONNECTOR_REJECTED_VERIFICATION = 5510,
  STATE_CONNECTOR_REJECTED_VERIFICATION_FAILED = 5511,
  STATE_CONNECTOR_REJECTED_READY_TO_ONBOARD = 5512,
  STATE_CONNECTOR_REJECTED_ONBOARDED = 5513
}

export const ConnectorWaitStates = [
  JobFlowStatus.STATE_CONNECTOR_OFFER_ACCEPTED,
  JobFlowStatus.STATE_CONNECTOR_ONBOARDING,
  JobFlowStatus.STATE_CONNECTOR_FUTURE_ONBOARDING,
  JobFlowStatus.STATE_CONNECTOR_OFFBOARDING,
  JobFlowStatus.STATE_CONNECTOR_CONTRACT_EDITED,
  JobFlowStatus.STATE_CONNECTOR_REOPEN,
  JobFlowStatus.STATE_CONNECTOR_FUTURE_ENDED,
  JobFlowStatus.STATE_CONNECTOR_REJECTED_VERIFICATION,
  JobFlowStatus.STATE_CONNECTOR_REJECTED_VERIFICATION_FAILED,
  JobFlowStatus.STATE_CONNECTOR_REJECTED_READY_TO_ONBOARD,
  JobFlowStatus.STATE_CONNECTOR_REJECTED_ONBOARDED
]
export const isInVndlyProcessingState = (process_state: number) =>
  process_state === JobFlowStatus.STATE_PRE_ONBOARD_TRANSITION_PROCESSING

export const getPayTypeSuffixLazy = (value: PayType | undefined): ReactNode => value && PayTypeSuffixLazy[value]

export const getPayTypeDisplaySuffixLazy = (value: PayTypeDisplay | undefined): ReactNode =>
  value && PayTypeDisplaySuffixLazy[value]

/**
 * Deprecated (not translated).  Use getPayTypeSuffixLazy instead
 */
export const getPayTypeSuffix = (value: PayType | PayTypeDisplay | undefined): PayTypeSuffix | undefined =>
  value && PAY_TYPE_SUFFIX_MAP[value]

export const getPayTypeDisplay = (value: PayTypeDisplay | PayType | undefined): ReactNode | undefined => {
  return value && lazyMsg(PAY_TYPE_DISPLAY_MAP[value])
}

export enum JobHireTypeDisplay {
  DIRECT_HIRE = 'Direct Hire',
  CONTRACTOR = 'Contractor'
}

export enum HireType {
  DIRECT_HIRE = 1,
  CONTRACTOR = 2
}

export const HIRE_TYPE_DISPLAY_TOKEN = {
  [HireType.DIRECT_HIRE]: lazyMsg('term.direct_hire', { count: 1 }),
  [HireType.CONTRACTOR]: lazyMsg('term.contractor', { count: 1 })
}

export enum JobEndReason {
  CANDIDATE_NOT_FOUND = '1',
  POSITION_FILLED = '2',
  OTHER = '3',
  /** @deprecated Not really used anywhere */
  POSITION_CANCELED = '4',
  DUPLICATE = '5',
  JOB_CANCELLED = '6'
}

export enum JobEndReasonDisplay {
  CANDIDATE_NOT_FOUND = 'Candidate not found',
  POSITION_FILLED = 'Filled',
  /** @deprecated Not really used anywhere */
  POSITION_CANCELED = 'Position cancelled',
  OTHER = 'Other',
  DUPLICATE = 'Duplicate',
  JOB_CANCELLED = 'Cancelled',
  AUTOMATIC = 'Filled automatically' // No associated number in JobEndReason
}

export enum ApplicationSubmissionLimitType {
  PER_JOB = 'Per Job',
  PER_POSITION = 'Per Position'
}

export enum JobFillProcedure {
  REGULAR = 1,
  FASTPATH = 2,
  PRE_IDENTIFIED = 3
}

// Use this type to create "component_type key to K value" objects
export type JobStatusDisplayMap<K> = { [name in JobStatusDisplay]: K }

type JobBase = {
  id: number
  title: string
  status: JobStatus
  reason?: JobEndReason
  updated_by: number
  updated_at: string
  created_by: number
  created_at: string
}

export enum JobRateType {
  MIN_MAX_RATE = 'min_max_rate',
  MIN_MAX_SALARY = 'min_max_salary',
  RATE = 'rate',
  MILESTONE = 'milestone'
}

export type JobRate = {
  type: JobRateType
  rate?: number
  pay_rate?: number
  min_rate?: number
  max_rate?: number
  min_pay_rate?: number
  max_pay_rate?: number
  min_salary?: number
  max_salary?: number
  markup?: number
  min_hours?: number
  not_qualified_rate?: number
  unit_name: string
  currency_code: string
  label: string
}

export type BillingDetails = {
  billing_contact_label?: string
  billing_contact_name?: string
  billing_address_label?: string
  billing_address?: string
}

export type CollegeboardFields = {
  assignment_label?: string
  assignment?: string
  hr_reference_label?: string
  hr_reference?: string
  cost_center_label?: string
  cost_center?: string
}

type VendorDistribution = {
  id: number
  status: VendorDistroJobResponseStatus
}

export enum BULK_ACTION_RESULT_STATUS {
  SUCCESS = 'success',
  PARTIAL_SUCCESS = 'partial_success',
  FAILURE = 'failure'
}

export enum BULK_ACTION_ID {
  BULK_REJECT = 'job_application_bulk_reject',
  BULK_ONBOARD = 'wo_bulk_actions_onboard'
}

export type BulkActionRecordDetails = {
  num_total_rows?: number
  is_bulk_action_complete?: boolean
  is_user_the_bulk_action_creator?: boolean
  num_successful_rows?: number
  num_partial_success_rows?: number
  num_failed_rows?: number
  status?: BULK_ACTION_RESULT_STATUS
  bulk_action_id?: BULK_ACTION_ID
}

export interface JobPreview extends JobBase {
  application_count: number
  job_rate: JobRate
  contract_pay_type: PayType
  rate_type: RateType
  is_pay_rate_driven?: boolean
  business_unit: number
  business_unit_display: string
  calculation_pack: number
  created_by_name: string
  currency_code: string
  end_date: string
  fast_path_candidate_status?:
    | JobApplicationStatus.VendorVerificationInProcess
    | JobApplicationStatus.ContractedOnboarded
    | JobApplicationStatus.ReadyToOnboard
  hire_type_display: JobHireTypeDisplay
  is_fast_path: boolean
  is_favorite: boolean
  is_rejected: boolean
  job_category_display: string
  job_summary: string
  job_template_job_type: string
  location: string
  location_id: number
  markup: number
  no_of_position: number
  pay_rate: number
  pay_type: PayTypeDisplay
  program_rate_type?: ProgramRateType
  preidentified_count: number
  program_team_names: string[]
  reason_display?: JobEndReasonDisplay
  rejected_by?: string
  resource_manager: number
  resource_manager_name: string
  total_no_of_position: number
  shortlisted_count: number
  start_date: string
  status_display: JobStatusDisplay
  vendor_distribution?: VendorDistribution
  can_vendor_dist_be_acknowledged: boolean
  shift_strategy?: string
  shifts?: string[]
}

export interface JobApplyModalDetail extends JobPreview {
  apply_maximum_no_of_applications_per_vendor_limit: boolean
  application_submission_limit_type: ApplicationSubmissionLimitType
  calculation_pack: number
  contract_pay_type: number
  job_category_id: number
  job_template_id: number
  job_template_job_type_id?: number
  maximum_no_of_applications_per_vendor: number
  maximum_no_of_applications_per_vendor_for_job: number
  pay_parity_rate: number | string
  total_no_of_onboarded_position: number
  total_no_of_closed_workers: number
  previously_applied_candidates: string[]
  no_of_previously_applied_not_discarded_candidates: number | null
  is_billable: boolean
  is_pay_rate_driven?: boolean
  should_show_rates: boolean
  should_show_candidate_compensation: boolean
  should_show_dates_overwrite: boolean
  should_show_location_overwrite: boolean
}

export interface Job extends JobBase {
  application_count: number
  application_submission_limit_type: number
  apply_maximum_no_of_applications_per_vendor_limit: boolean
  business_unit?: number
  calendar_pay_profile?: number
  contract_pay_type: PayType
  rate_type: RateType
  calculation_pack: number
  currency_code: string
  end_date: Date | string
  end_reason_msg?: string
  expense_approver?: number[]
  expense_type?: 'yes' | 'no'
  hire_type: HireType
  hiring_team?: number[]
  is_closed: boolean
  is_on_hold: boolean
  is_pending_approval: boolean
  is_pending_connector: boolean
  is_vendor_assigned: boolean
  job_category: number
  job_template: number
  job_template_job_type: number
  job_template_job_type_display?: string
  job_template_is_active?: boolean
  location: number
  markup: number
  maximum_no_of_applications_per_vendor: number
  no_of_position: number
  number_of_pending_positions: number
  overtime_profile?: number
  program_rate_type: ProgramRateType
  rate_calculator?: RateCalculatorData | null
  resource_manager: number
  start_date: Date | string
  status_display: JobStatusDisplay
  sub_reason_text?: string
  timesheet_approver?: number[]
  total_no_of_onboarded_position: number
  total_no_of_position: number
  work_type_profile?: number
  work_week: number // Standard hours per week
  vendor_distribution?: VendorDistribution
  vendor_payment_term?: VendorPayment
  job_rate: JobRate
  is_pay_rate_driven?: boolean
  work_week_start_time?: string
  shift_details?: ShiftApiData[]
  event_based_expense_details?: EventBasedExpensesListApiData[]
  default_event_based_expense_cc_data?: ChargeCodeRawValues
}

export interface JobOverview extends JobBase {
  external_id?: string
  company_name?: string
  project_site_name?: string
  fast_path_candidate_status?:
    | JobApplicationStatus.VendorVerificationInProcess
    | JobApplicationStatus.ContractedOnboarded
    | JobApplicationStatus.ReadyToOnboard
  weekly_paid_travel: string
  job_fill_procedure: JobFillProcedure
  business_unit: number
  business_unit_name?: string
  business_unit_level?: number
  business_unit_parent_name?: string
  calendar_pay_profile_title?: string
  contract_pay_type: PayType
  contract_type: ContractType[]
  conversion_pay_type?: ConversionType
  expense_type?: 'yes' | 'no'
  hire_type: HireType
  rate_type: RateType
  shift_strategy?: ShiftStrategy
  shift_display_names?: string[]
  currency_code: string
  base_currency_code: string
  end_date: Date | string
  provisioning_eligible: boolean
  job_category_name: string
  job_code?: string
  job_template_job_type_display?: string
  location: number
  no_of_position: number
  overtime_profile_title?: string
  hiring_team_names?: string[]
  program_team_names?: string[]
  timesheet_approver_names?: string[]
  expense_approver_names?: string[]
  program_rate_type: ProgramRateType
  project_details?: string
  resource_manager: number
  resource_manager_name: string
  start_date: Date | string
  work_type_profile_title?: string
  work_week: number
  job_rate: JobRate
  is_pay_rate_driven?: boolean
  min_experience?: number
  max_experience?: number
  min_salary?: number
  max_salary?: number
  min_rate?: number
  max_rate?: number
  expected_min_salary?: number
  expected_max_salary?: number
  expected_min_rate?: number
  expected_max_rate?: number
  min_suggested_rate?: number
  max_suggested_rate?: number
  preferred_rate?: number
  pay_parity_rate?: number
  pay_group?: string
  premium_rate_name?: string
  chose_premium_rate: boolean
  calculation_pack: number
  total_budget: number
  base_budget?: number
  billing_details?: BillingDetails
  billing_mode?: BillingMode
  billing_schedule_name?: string | null
  ot_applicability: OtApplicability
  legacy_jobmetadata_fields: CollegeboardFields
}

export interface JobForJobAppList {
  business_unit?: number
  contract_pay_type?: PayType
  currency_code?: string
  hire_type?: HireType
  id?: number
  is_latest_bulk_action_record_read?: boolean
  latest_bulk_action_record?: number
  latest_bulk_action_record_details?: BulkActionRecordDetails
  status?: JobStatus
  number_of_open_positions?: number
  is_workday_position_managed?: boolean
}

export enum ModuleType {
  CONTINGENT = 0,
  SOW = 1,
  WPM = 2
}
interface JobModuleResponse {
  results: JobModule[]
}
export interface JobModule {
  value: ModuleType
  label: string
}
export type RecentJobsParams = {
  module?: number
  offset?: number
  limit?: number
  search_title?: string
}
export type JobsResponse = OffsetLimitPaginationResponse<JobPreview>
export type JobOrderingOption = 'title' | 'updated_at' | 'id' | 'created_at' | '-created_at'
export type JobsListParams = Partial<OffsetLimit> &
  RecentJobsParams & {
    ordering?: JobOrderingOption
    search?: string
    overtime_profile?: number
    count_only?: boolean
    status?: string
    job_template_is_active?: boolean
  }

export const jobKeys = {
  all: ['jobs'] as const,
  lists: () => [...jobKeys.all, 'list'] as const,
  list: (filters: JobsListParams = {}) => [...jobKeys.lists(), filters] as const,
  listNonEndedCount: (filters: JobsListParams = {}) => [...jobKeys.lists(), 'non-ended-count', filters] as const,
  details: () => [...jobKeys.all, 'detail'] as const,
  detail: (id?: number, filters?: JobParams) => [...jobKeys.all, 'detail', id, filters] as const,
  view: (id?: number, filters?: JobParams) => [jobKeys.all, 'view', id, filters] as const,
  detailJobAppList: (id?: number) => [...jobKeys.all, 'detail', 'job-app-list', id] as const,
  compact: (id?: number) => [...jobKeys.all, 'detail', 'compact', id] as const,
  compactSow: (id?: number, sowId?: number, sowVersionId?: number) =>
    [...jobKeys.all, 'detail', 'compact-sow', id, { sowId, sowVersionId }] as const
}

export const JOB_QUERY = 'jobs'
export const useJobListQuery = (queryParams: JobsListParams, opts: UseQueryOptions<JobsResponse>) => {
  const key = jobKeys.list(queryParams)
  return useQuery({ queryKey: key, queryFn: () => jobsApi.list(queryParams), ...opts, keepPreviousData: true })
}

export const PUBLISH_JOB_HELP_TEXT = 'publish_job_help_text'
export const usePublishJobHelpTextQuery = () => {
  return useQuery({ queryKey: [PUBLISH_JOB_HELP_TEXT], queryFn: () => jobsApi.getPublishJobHelpText() })
}

export const JOB_TEMPLATE_CONFIG = 'job_template_config'
export const useJobTemplateConfigQuery = () => {
  return useQuery({ queryKey: [JOB_TEMPLATE_CONFIG], queryFn: () => jobsApi.getJobTemplateConfig() })
}

export const useJobListInfiniteQuery = (queryParams: JobsListParams) => {
  const key = jobKeys.list(queryParams)
  return useInfiniteQuery({
    queryKey: key,
    queryFn: ({ pageParam = 0 }) =>
      jobsApi.list({ ...queryParams, module: ModuleType.CONTINGENT, limit: 30, offset: Number(pageParam) }),
    getNextPageParam: lastPage => lastPage.next_params?.offset ?? false
  })
}

export const useJobListCompactDetailsQuery = (id?: number) => {
  const key = jobKeys.compact(id)
  return useQuery({ queryKey: key, queryFn: () => jobsApi.listDetail(id!), enabled: Number.isInteger(id) })
}

export const useSowJobListCompactDetailsQuery = (
  id?: number,
  sowId?: number,
  sowVersionId?: number,
  { enabled = true } = {}
) => {
  const key = jobKeys.compactSow(id, sowId, sowVersionId)
  return useQuery({
    queryKey: key,
    queryFn: () => sowJobApi.getJobDetailApply(sowId!, sowVersionId!, id!),
    enabled: Number.isInteger(id) && Number.isInteger(sowId) && Number.isInteger(sowVersionId) && enabled
  })
}
export const invalidateSowJobListCompactDetailsQuery = (id?: number, sowId?: number, sowVersionId?: number) => {
  return queryClient.invalidateQueries(jobKeys.compactSow(id, sowId, sowVersionId))
}

export const useJobListNonEndedCountQuery = (queryParams: JobsListParams, opts: UseQueryOptions<number>) => {
  const key = jobKeys.listNonEndedCount(queryParams)
  return useQuery({ queryKey: key, queryFn: () => jobsApi.listNonEndedCount(queryParams), ...opts })
}

export type JobParams = {
  sow?: boolean
  sow_version?: number
}

export const useJobQuery = (id?: number, queryParams: JobParams = {}, queryOptions?: UseQueryOptions<Job>) => {
  const key = jobKeys.detail(id, queryParams)
  return useQuery({
    queryKey: key,
    queryFn: () => jobsApi.get(id!, queryParams),
    enabled: Number.isInteger(id),
    ...queryOptions
  })
}
export const useJobViewQuery = (
  id?: number,
  queryParams: JobParams = {},
  queryOptions?: UseQueryOptions<JobOverview>
) => {
  const key = jobKeys.view(id, queryParams)
  return useQuery({
    queryKey: key,
    queryFn: () => jobsApi.view(id!, queryParams),
    enabled: Number.isInteger(id),
    ...queryOptions
  })
}
export const useJobForJobAppListQuery = (id?: number, queryOptions?: UseQueryOptions<JobForJobAppList>) => {
  const key = jobKeys.detailJobAppList(id)
  return useQuery({
    queryKey: key,
    queryFn: () => jobsApi.getForJobAppList(id!),
    enabled: Number.isInteger(id),
    ...queryOptions
  })
}

export const invalidateJobForJobAppListQuery = (id: number) => {
  return queryClient.invalidateQueries(jobKeys.detailJobAppList(id))
}

/**
 * TODO This hook and useJobQuery above are too similar... we should have one job-fetching hook
 */
export const useJobDependentQuery = (
  id: number | undefined,
  queryParams: JobParams = {},
  queryOptions: UseQueryOptions<Job> = {}
) => {
  return useQuery({
    queryKey: [JOB_QUERY, id, queryParams],
    queryFn: () => jobsApi.get(id!, queryParams),
    enabled: !!id,
    ...queryOptions
  })
}

export type FavoriteJobResponse = {
  job_id: number
  is_favorite: boolean
}
export type HoldResponse = {
  message: string
}

export const useFavoriteJobToggleMutation = (opts: UseMutationOptions<FavoriteJobResponse, unknown, number>) =>
  useMutation({ mutationFn: (jobId: number) => jobsApi.toggleFavorite(jobId), ...opts })

export const useHoldToggleMutation = (opts: UseMutationOptions<FavoriteJobResponse, unknown, number>) =>
  useMutation({ mutationFn: (jobId: number) => jobsApi.toggleHold(jobId), ...opts })

export type ReopenJobParams = {
  numberOfPositions: number
  notifyVendors: boolean
  jobId: number
}

export const useReopenJobMutation = (opts: UseMutationOptions<void, unknown, ReopenJobParams> = {}) =>
  useMutation({ mutationFn: (data: ReopenJobParams) => jobsApi.reopenJob(data.jobId, data), ...opts })

export const useCanCreateJobs = () => {
  return useQuery({
    queryKey: ['jobs/permissions'],
    queryFn: (): Promise<{ can_create_jobs: boolean }> => fetch.get('/api/v2/jobs/permissions')
  })
}

export const useJobActivityLogs = (id: number, queryOptions?: UseQueryOptions<ActivityLogType[]>) => {
  return useQuery({
    queryKey: [`jobs/${id}/activity_log`],
    queryFn: () => jobsApi.getActivityLogs(id),
    ...queryOptions,
    enabled: Number.isInteger(id)
  })
}

export const useJobConfig = () => {
  return useQuery({ queryKey: ['jobs/config'], queryFn: () => jobsApi.getConfig(), staleTime: 1000 * 60 * 5 })
}

export const useJobCompensationSettings = () => {
  return useQuery({
    queryKey: [`jobs/compensation/settings`],
    queryFn: () => jobsApi.getJobCompensationSettings(),
    staleTime: 1000 * 60 * 5
  })
}

export const useFormConfigForBu = (buId?: number, queryOptions?: UseQueryOptions<JobFormConfigForBu>) => {
  return useQuery({
    queryKey: [`jobs/job_form_config_for_bu/${buId}`],
    queryFn: () => jobsApi.getFormConfigForBu(buId!),
    ...queryOptions,
    enabled: Number.isInteger(buId)
  })
}

export const useJobRecordConfig = (id: number, queryOptions?: UseQueryOptions<JobConfig>) => {
  return useQuery({ queryKey: [`jobs/${id}/config`], queryFn: () => jobsApi.config(id), ...queryOptions })
}

export const useJobModuleQuery = (queryOptions?: UseQueryOptions<JobModuleResponse>) => {
  return useQuery({ queryKey: ['jobs_modules'], queryFn: () => jobsApi.getModuleTypes(), ...queryOptions })
}

/**
 * Job Attachments API
 */
export const jobAttachmentsAPI = {
  get: (id: number, urlParams: Record<string, any>) => fetch.get(getJobAttachmentsURL(id, urlParams)),
  save: ({ id, formData }: { id: number; formData: any }) =>
    fetch.postFile(getJobAttachmentsURL(id), formData, { noContentType: true }),
  delete: ({ id, resourceId }: { id: number; resourceId: number }) =>
    fetch.delete(`${getJobAttachmentsURL(id)}${resourceId}`)
}

export function getJobAttachmentsURL(id: number, urlParams = {}) {
  return Object.keys(urlParams).length > 0
    ? `/api/v2/jobs/job/${id}/attachments/?${qs.stringify(urlParams)}`
    : `/api/v2/jobs/job/${id}/attachments/`
}

if (isClient()) {
  Object.assign(window, { jobAttachmentsSave: jobAttachmentsAPI.save })
}

export const useJobAttachmentsQuery = (id: number, urlParams = {}) =>
  useQuery({
    queryKey: ['jobAttachments', { id, urlParams }],
    queryFn: () => jobAttachmentsAPI.get(id, urlParams),
    keepPreviousData: true
  })

export const useJobAttachmentsAddMutation = () =>
  useMutation({
    mutationFn: jobAttachmentsAPI.save,
    onSuccess: async () => {
      await queryClient.invalidateQueries(['jobAttachments'])
    }
  })

export const useJobAttachmentsDeleteMutation = () =>
  useMutation({
    mutationFn: jobAttachmentsAPI.delete,
    onSuccess: async () => {
      await queryClient.invalidateQueries(['jobAttachments'])
    }
  })
/**
 * Job Application Stats
 */

export type JobApplicationStats = {
  count: number
  current_status_counts: { [key in keyof typeof JobApplicationStatus]?: number }
  offer_status_count: { [key in keyof typeof OfferStatus]?: number }
}

const JOB_APPLICATION_STATS_QUERY = 'jobApplicationStats'
export const useJobApplicationStatsQuery = (id: number) => {
  return useQuery({ queryKey: [JOB_APPLICATION_STATS_QUERY, id], queryFn: () => jobsApi.getApplicationStats(id) })
}

/**
 * Direct Hire Payment, Fee,  API
 */
export const DIRECT_HIRE_PAYMENT_URL = '/jobs/direct-hire-payments/'
export const directHirePaymentApi = {
  getPaymentsAndSalary: (data: DirectHirePaymentAndSalaryData): Promise<DirectHirePaymentsAndSalaryResponse> =>
    fetch.post(DIRECT_HIRE_PAYMENT_URL, data)
}

export type DirectHirePaymentsAndSalaryResponse = {
  salary: string
  payments: Payment[]
  fee_schedule: Fee[]
}

export type Fee = {
  date: Date
  amount: number | null
}

export type Payment = {
  week: number
  date: string
  amount: number
}

export type DirectHirePaymentAndSalaryData = {
  salary?: number
  start_date: string
  job_id: number
  job_application_id: number
  rate?: number
  standard_hours: number | null
}

export const useDirectHirePaymentsAndSalaryQuery = (
  data: DirectHirePaymentAndSalaryData,
  opts?: UseQueryOptions<DirectHirePaymentsAndSalaryResponse>
) => {
  return useQuery({
    queryKey: ['direct_hire_payment_salary', data],
    queryFn: () => directHirePaymentApi.getPaymentsAndSalary(data),
    enabled: Boolean(data.rate && data.standard_hours),
    ...opts
  })
}

export interface CandidateProfileShareData extends ShareProfileModalFormData {
  link_path: string
  link_app_id: number
}

export const useCandidateProfileShareMutation = () =>
  useMutation({ mutationFn: (data: CandidateProfileShareData) => jobsApi.shareCandidateProfile(data) })

export interface PayTypeOption {
  value: number
  label: string
}
export interface JobTypeOption {
  id: number
  name: string
}

export type ListJobCategoriesResponse = JobCategories[]

export interface JobCategories {
  id: number
  name: string
  selectable: boolean
  parent: number | null
  children: number[]
  fee_profile: number | null
  ancestors_including_self: number[]
}

export type BulkOnboardCreatorReadResponse = {
  job_id: number
  is_latest_bulk_action_record_read: boolean
}

export const useSetBulkActionCreatorReadMutation = (
  opts: UseMutationOptions<BulkOnboardCreatorReadResponse, unknown, number>
) => useMutation({ mutationFn: (jobId: number) => jobsApi.setBulkActionCreatorRead(jobId), ...opts })

export type CreateCustomJobReponse = {
  id: number
  title: string
}

export const useCreateCustomJobMutation = (opts: UseMutationOptions<CreateCustomJobReponse, unknown, string> = {}) =>
  useMutation({ mutationFn: (title: string) => jobsApi.createCustomJob(title), ...opts })

export const transformApplyCandidateToAPIShape = (
  candidates: (Omit<Omit<ApplicableCandidate, 'start_date'>, 'end_date'> & {
    start_date?: Date | string | null
    end_date?: Date | string | null
  })[]
) =>
  candidates.map(c => ({
    candidate: c.id,
    wo_data: {
      bill_rate: c.period_bill_rate,
      pay_rate: c.period_pay_rate,
      // back end expects a decimal, not a percentage
      markup: floatToPercent(c.period_markup ?? 0).toFixed(12),
      location: c.location,
      start_date: c.start_date ? formatDataDate(c.start_date) : undefined,
      end_date: c.end_date ? formatDataDate(c.end_date) : undefined,
      calculation_pack: c.calculation_pack
    }
  }))

export const useApplyCandidatesToJobMutation = (
  jobDetails?: JobApplyModalDetail,
  options?: UseMutationOptions<ApplyJobApplicationResponse, JobApplicationApplyError, JobApplicationOptions>
) => {
  return useMutation({
    mutationFn: async ({ candidates, isPreIdentified = false }: JobApplicationOptions) => {
      if (jobDetails?.id === undefined) throw new Error('Job ID is undefined')

      const data = transformApplyCandidateToAPIShape(candidates)

      // eslint-disable-next-line consistent-return
      return jobsApi.applyCandidates(jobDetails.id!, { data, is_pre_identified: isPreIdentified })
    },
    ...options,
    onSuccess(...args) {
      if (options?.onSuccess !== undefined) options.onSuccess(...args)
      queryClient.invalidateQueries([JOB_QUERY])
    }
  })
}
